/**
 * API Response Validation
 *
 * Zod-based runtime validation for API responses.
 * Catches type mismatches that TypeScript can't detect at runtime.
 *
 * Features:
 * - Runtime validation with clear error messages
 * - STRICT mode: fails on schema mismatches (no silent passthrough)
 * - Development warnings, production graceful degradation
 *
 * CRITICAL: Schemas must NOT use .passthrough() for new code.
 * Any schema mismatch should fail validation, not be silently ignored.
 */

import { z } from 'zod/v4';
import type { ZodType } from 'zod/v4';

// Type aliases for v4 compatibility
type ZodSchema<T = unknown> = ZodType<T>;

/**
 * Validation result type
 */
export type ValidationResult<T> =
  { success: true; data: T } | { success: false; error: z.ZodError; data: null };

/**
 * Validate and transform API response
 *
 * @param schema - Zod schema to validate against
 * @param data - Raw API response data
 * @param options - Validation options
 * @returns Validated and transformed data, or null on failure
 *
 * @example
 * ```ts
 * const RecordSchema = z.object({
 *   id: z.string().uuid(),
 *   projectId: z.string().uuid(),
 *   status: z.enum(['pending', 'completed', 'failed']),
 * });
 *
 * const result = validateApiResponse(RecordSchema, apiData, {
 *   endpoint: '/api/v1/records/123',
 *   transformKeys: true,
 * });
 *
 * if (result.success) {
 *   console.log(result.data); // Fully typed!
 * }
 * ```
 */
export function validateApiResponse<T>(
  schema: ZodSchema<T>,
  data: unknown,
  options: {
    endpoint?: string;
    throwOnError?: boolean;
  } = {}
): ValidationResult<T> {
  const { endpoint = 'unknown', throwOnError = false } = options;

  try {
    // Validate with Zod (API already returns camelCase)
    const validated = schema.parse(data);

    return { success: true, data: validated };
  } catch (error) {
    if (error instanceof z.ZodError) {
      // Format error message
      const zodError = error as z.ZodError;
      const issues = zodError.issues
        .map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`)
        .join('\n');

      const message = `[API Validation Error] ${endpoint}\n${issues}`;

      // Always log in development
      if (import.meta.env.DEV) {
        console.error(message);
        console.error('Raw data:', JSON.stringify(data, null, 2).slice(0, 500));
      }

      // Optionally throw
      if (throwOnError) {
        throw new Error(message);
      }

      return { success: false, error: zodError, data: null };
    }
    throw error;
  }
}

/**
 * Create a validated fetch wrapper for a specific schema
 *
 * @example
 * ```ts
 * const fetchRecord = createValidatedFetcher(RecordSchema, authenticatedFetch);
 * const record = await fetchRecord('/api/v1/records/123');
 * // record is fully typed and validated!
 * ```
 */
export function createValidatedFetcher<T>(
  schema: ZodSchema<T>,
  fetcher: <R>(url: string, options?: RequestInit) => Promise<R>
) {
  return async (url: string, fetchOptions?: RequestInit): Promise<T | null> => {
    const rawData = await fetcher<unknown>(url, fetchOptions);
    const result = validateApiResponse(schema, rawData, {
      endpoint: url,
    });

    if (!result.success) {
      return null;
    }

    return result.data;
  };
}

// ============================================
// Common Zod Helpers
// ============================================

/**
 * UUID string schema
 */
export const zodUuid = z.string().uuid();

/**
 * ISO date string schema (accepts both ISO and simple date formats)
 */
export const zodDateString = z
  .string()
  .refine((val) => !isNaN(Date.parse(val)), { message: 'Invalid date string' });

/**
 * Nullable wrapper - makes field accept null
 */
export const nullable = <T extends ZodSchema>(schema: T) => schema.nullable();

/**
 * API response wrapper schema
 * Most API responses follow { success: boolean, [dataKey]: T } pattern
 */
export function apiResponseSchema<T extends ZodSchema>(dataKey: string, dataSchema: T) {
  return z.object({
    success: z.boolean().optional(),
    [dataKey]: dataSchema,
  });
}

// ============================================
// Type inference helper
// ============================================

/**
 * Infer TypeScript type from Zod schema
 *
 * @example
 * ```ts
 * const RecordSchema = z.object({ id: z.string() });
 * type Record = InferSchema<typeof RecordSchema>;
 * // Record = { id: string }
 * ```
 */
export type InferSchema<T extends ZodSchema> = z.infer<T>;
